import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  TextField,
} from '@mui/material';
import { X, AlertTriangle, Info, CheckCircle, AlertCircle } from 'lucide-react';

type ConfirmationType = 'warning' | 'danger' | 'info' | 'success';

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  data?: {
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    type?: ConfirmationType;
    onConfirm?: (reason?: string) => void | Promise<void>;
    reasonInput?: {
      label?: string;
      placeholder?: string;
      required?: boolean;
    };
  };
}

/**
 * ConfirmationModal
 * Generic confirmation dialog for various actions
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ open, onClose, data }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  const handleConfirm = async () => {
    if (data?.onConfirm) {
      setIsLoading(true);
      try {
        const trimmed = reason.trim();
        await data.onConfirm(trimmed ? trimmed : undefined);
      } finally {
        setIsLoading(false);
      }
    }
    onClose();
  };

  const getIcon = () => {
    const iconProps = { size: 24 };
    switch (data?.type) {
      case 'danger':
        return <AlertTriangle {...iconProps} color="#d32f2f" />;
      case 'warning':
        return <AlertCircle {...iconProps} color="#ed6c02" />;
      case 'success':
        return <CheckCircle {...iconProps} color="#2e7d32" />;
      case 'info':
      default:
        return <Info {...iconProps} color="#0288d1" />;
    }
  };

  const getButtonColor = (): 'error' | 'warning' | 'success' | 'info' | 'primary' => {
    switch (data?.type) {
      case 'danger':
        return 'error';
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      case 'info':
        return 'info';
      default:
        return 'primary';
    }
  };

  const getBackgroundColor = () => {
    switch (data?.type) {
      case 'danger':
        return 'error.lighter';
      case 'warning':
        return 'warning.lighter';
      case 'success':
        return 'success.lighter';
      case 'info':
      default:
        return 'info.lighter';
    }
  };

  const reasonRequired = data?.reasonInput?.required ?? false;
  const showReason = !!data?.reasonInput;
  const reasonInvalid = reasonRequired && reason.trim().length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            {data?.title || 'Confirm Action'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            py: 2,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: getBackgroundColor(),
              mb: 2,
            }}
          >
            {getIcon()}
          </Box>
          <Typography variant="body1" color="text.secondary">
            {data?.message || 'Are you sure you want to proceed with this action?'}
          </Typography>
        </Box>
        {showReason && (
          <TextField
            fullWidth
            multiline
            rows={3}
            label={data?.reasonInput?.label ?? 'Reason'}
            placeholder={data?.reasonInput?.placeholder ?? 'Add a reason...'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required={reasonRequired}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center', gap: 1 }}>
        <Button onClick={onClose} disabled={isLoading} variant="outlined" sx={{ minWidth: 100 }}>
          {data?.cancelText || 'Cancel'}
        </Button>
        <Button
          variant="contained"
          color={getButtonColor()}
          onClick={handleConfirm}
          disabled={isLoading || reasonInvalid}
          sx={{ minWidth: 100 }}
        >
          {isLoading ? 'Processing...' : data?.confirmText || 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationModal;
